'use strict';

/**
 * Single event broadcast when the buyer's resolved location changes.
 *
 * Emitted by:
 *   - `geolocation.js` after Geo-IP / browser-geolocation resolution writes the cache
 *   - `dealer_selection_modal.js` after a successful manual zip submit
 *
 * Subscribed by:
 *   - `cart_subtotal.js` (estimated-shipping inline ZIP stays aligned with buyer location)
 *   - `dealer_selection_modal.js` (refetch dealers when modal is open)
 *   - `location.js` (header)
 *   - `product_purchase.js` (PDP callout / shipping)
 *
 * Contract: emitters that **persist** a new buyer location MUST write `LOCATION_CACHE_KEY`
 * (see cache-config.js) before triggering so subscribers can rely on cache and event being consistent.
 *
 * **Exception — cold resolution finished with no location:** `geolocation.js` may emit
 * `LOCATION_CHANGED_EVENT` with `resolutionTerminal: true` and `hasResolvedLocation: false`
 * (empty `postalCode` / null `location`) so the header can clear its loading state. Do not write
 * the location cache for that payload. If `LOCATION_CACHE_KEY` already holds a resolved buyer
 * location (e.g. Geo-IP ran before a failed browser geolocation attempt), that terminal payload
 * is skipped so the header is not cleared. When the server returns a location without a
 * displayable postal while `has_browser_tier` or manual source applies, terminal is still emitted
 * if nothing was broadcast so the header can exit its loading state.
 *
 * Legacy note: `geolocationComplete` is not used in this repository; lead-gen location flows
 * rely on this event and the `resolutionTerminal` payload when Geo-IP / browser resolution yields
 * no postal to display.
 *
 * @typedef {object} LocationChangedPayload
 * @property {boolean} [hasResolvedLocation] When `resolutionTerminal` is true, false means Geo-IP / browser flow ended with nothing to show.
 * @property {object|null} location            Raw IPLocation values (postal_code, city, region_code, ...).
 * @property {string} postalCode
 * @property {boolean} [resolutionTerminal]  True when a resolution attempt finished without a persisted location (header idle).
 * @property {'browser'|'ip'|'manual'|null} [source]  See `location-source.js`.
 *
 * Usage:
 *   const { LOCATION_CHANGED_EVENT } = require('public/js/lib/location-events');
 *   rp$(document).trigger(LOCATION_CHANGED_EVENT, [payload]);
 *   rp$(document).on(LOCATION_CHANGED_EVENT, (evt, payload) => { ... });
 */
const LOCATION_CHANGED_EVENT = 'location:changed';

module.exports = { LOCATION_CHANGED_EVENT };
